import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';

// ============================================================
// Storage — Railway volume diagnostics + reclamation (super_admin).
// Shows exactly what the volume holds (DB, WAL, backup, uploads),
// how much space a compact would reclaim, and the biggest tables.
// Actions: retention sweep (log tables only) and VACUUM compact.
// ============================================================

function fmtBytes(b) {
  if (b == null) return '—';
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  if (b >= 1e3) return `${(b / 1e3).toFixed(0)} KB`;
  return `${b} B`;
}

function Stat({ label, value, accent }) {
  return (
    <div className="card" style={{ padding: '14px 18px', minWidth: 150, flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || 'var(--text-primary)', letterSpacing: '-0.01em' }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function StoragePage() {
  const [report, setReport] = useState(null);
  const [photoEst, setPhotoEst] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setReport(await api.getStorageReport());
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.getPhotoTierEstimate().then(setPhotoEst).catch(() => setPhotoEst({ error: true })); }, [report]);

  if (error && !report) return <div className="admin-page"><p className="alert alert-error">{error}</p></div>;
  if (!report) return <div className="admin-page"><p>Loading…</p></div>;

  const f = report.files;
  const totalTracked = f.database + f.wal + f.shm + f.backup_plain + f.backup_gz + f.uploads;
  const reclaimable = report.db_internals.reclaimable_bytes;
  const pendingSweep = report.retention.reduce((s, r) => s + (r.pending || 0), 0);
  const autoVacOn = report.db_internals.auto_vacuum_mode !== 0;

  async function act(fn, okMsg) {
    setBusy(true); setNotice(''); setError('');
    try {
      const res = await fn();
      setNotice(typeof okMsg === 'function' ? okMsg(res) : okMsg);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const rows = [
    ['Database', f.database, 'The live SQLite file — grows with calls, comments, logs'],
    ['Write-ahead log (WAL)', f.wal, 'Recent writes not yet folded into the main file'],
    ['Boot backup (gzipped)', f.backup_gz, 'Pre-migration safety copy, compressed ~5x'],
    ['Boot backup (plain)', f.backup_plain, 'Uncompressed copy — should be 0 shortly after each deploy'],
    ['Uploads (photos & files)', f.uploads, 'Compressed photos, receipts, attachments'],
  ];

  return (
    <div className="admin-page" style={{ maxWidth: 980 }}>
      <div className="page-header">
        <h2>Storage</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          What the Railway volume holds, and the levers to shrink it. Business records are never touched.
        </p>
      </div>

      {error && <p className="alert alert-error">{error}</p>}
      {notice && <p className="alert" style={{ background: '#dcfce7', color: '#166534' }}>{notice}</p>}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Volume in use" value={fmtBytes(report.volume ? report.volume.used : totalTracked)} />
        <Stat label="Live database" value={fmtBytes(f.database)} />
        <Stat label="Reclaimable by compact" value={fmtBytes(reclaimable)} accent={reclaimable > 100e6 ? '#b45309' : undefined} />
        <Stat label="Server RAM (RSS)" value={fmtBytes(report.process_memory.rss)} />
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>What's on the volume</div>
        {rows.map(([label, size, hint]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border-subtle, #edf2f7)' }}>
            <div style={{ minWidth: 210, fontWeight: 600, fontSize: 13.5 }}>{label}</div>
            <div style={{ minWidth: 90, fontWeight: 700, fontSize: 13.5, textAlign: 'right' }}>{fmtBytes(size)}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{hint}</div>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 0' }}>
          <div style={{ minWidth: 210, fontWeight: 800, fontSize: 13.5 }}>Total tracked</div>
          <div style={{ minWidth: 90, fontWeight: 800, fontSize: 13.5, textAlign: 'right' }}>{fmtBytes(totalTracked)}</div>
        </div>
      </div>

      {report.data_dirs && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Everything on /data, largest first</div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px' }}>
            The complete volume — whatever is biggest here is what's growing your Railway usage.
          </p>
          {report.data_dirs.map(d => (
            <div key={d.name} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border-subtle, #edf2f7)' }}>
              <code style={{ minWidth: 260, fontSize: 13 }}>{d.name}</code>
              <div style={{ minWidth: 90, fontWeight: 700, fontSize: 13.5, textAlign: 'right' }}>{fmtBytes(d.bytes)}</div>
              <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--bg-inset, #edf0f4)', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.max(1, Math.round((d.bytes / Math.max(1, report.data_dirs[0].bytes)) * 100))}%`,
                  height: '100%', borderRadius: 999,
                  background: 'linear-gradient(90deg, var(--brand-gold, #d97706), var(--brand-gold-light, #f59e0b))',
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {report.uploads_breakdown && (
        <div className="card" style={{ padding: 18, marginBottom: 16, borderLeft: '4px solid #dc2626' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Inside uploads/ — where the weight actually is</div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px' }}>
            Subfolders largest first, then the 15 biggest individual files anywhere under uploads/.
          </p>
          {report.uploads_breakdown.subfolders.map(d => (
            <div key={d.name} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border-subtle, #edf2f7)' }}>
              <code style={{ minWidth: 200, fontSize: 13 }}>{d.name}</code>
              <div style={{ minWidth: 90, fontWeight: 700, fontSize: 13.5, textAlign: 'right' }}>{fmtBytes(d.bytes)}</div>
              <div style={{ minWidth: 90, fontSize: 12, color: 'var(--text-muted)' }}>{d.files.toLocaleString('en-US')} files</div>
              <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--bg-inset, #edf0f4)', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.max(1, Math.round((d.bytes / Math.max(1, report.uploads_breakdown.subfolders[0].bytes)) * 100))}%`,
                  height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #dc2626, #f87171)',
                }} />
              </div>
            </div>
          ))}
          <div style={{ fontWeight: 700, fontSize: 12.5, marginTop: 14, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Biggest files</div>
          {report.uploads_breakdown.biggest_files.map(f => (
            <div key={f.path} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '4px 0', fontSize: 12.5 }}>
              <div style={{ minWidth: 80, fontWeight: 700, textAlign: 'right' }}>{fmtBytes(f.bytes)}</div>
              <code style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{f.path}</code>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 18, marginBottom: 16, borderLeft: '4px solid var(--brand-gold, #d97706)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>📷 Photo compression — the big lever</div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
              <strong>Raw rescue:</strong> iPhone photos (HEIC) silently skipped compression on upload and were stored at
              full 8–10 MB each — the actual volume weight. The sweep finds every oversized photo (any age) and compresses
              it to normal size (~95% smaller). <strong>Archive tier:</strong> photos older than {photoEst?.age_days ?? 60} days
              go to {photoEst?.archive_px ?? 800}px (still legible, ~60% smaller). Runs nightly; this button runs it now.
            </p>
          </div>
          {photoEst && !photoEst.error && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div className="card" style={{ padding: '10px 14px', minWidth: 120, borderColor: photoEst.raw_count > 0 ? '#fca5a5' : undefined }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: photoEst.raw_count > 0 ? '#b91c1c' : undefined }}>{(photoEst.raw_count || 0).toLocaleString('en-US')}</div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Raw uncompressed</div>
                <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{fmtBytes(photoEst.raw_bytes || 0)}</div>
              </div>
              <div className="card" style={{ padding: '10px 14px', minWidth: 120 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{photoEst.candidates.toLocaleString('en-US')}</div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To archive tier</div>
              </div>
              <div className="card" style={{ padding: '10px 14px', minWidth: 120 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: photoEst.estimated_savings_bytes > 50e6 ? '#b45309' : undefined }}>{fmtBytes(photoEst.estimated_savings_bytes)}</div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Est. reclaim</div>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-gold" style={{ fontWeight: 700 }} disabled={busy || !photoEst || photoEst.error || (photoEst.candidates === 0 && !photoEst.raw_count)}
            onClick={() => act(api.runPhotoTierSweep,
              res => `Rescued ${(res.rescued || 0).toLocaleString('en-US')} raw + archived ${res.archived.toLocaleString('en-US')} — reclaimed ${fmtBytes(res.saved_bytes)} in ${res.seconds}s${res.hit_budget ? ' (more next run — press again or wait for tonight)' : ''}${res.unreadable ? ` · ${res.unreadable} unreadable file(s) left as-is` : ''}`)}>
            {busy ? 'Working…' : (photoEst?.candidates === 0 && !photoEst?.raw_count) ? 'Nothing to shrink' : 'Compress photos now'}
          </button>
          {photoEst && !photoEst.error && photoEst.months.length > 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Covers {photoEst.months.length} month{photoEst.months.length === 1 ? '' : 's'}: {photoEst.months[0]} → {photoEst.months[photoEst.months.length - 1]}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }} className="grid-stack-mobile">
        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700 }}>🧹 Retention sweep</div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 10px' }}>
            Prunes operational logs only: SMS delivery logs older than 180 days, dispatcher
            activity older than 90 days. Runs automatically every night — this button runs it now.
          </p>
          {report.retention.map(r => (
            <div key={r.table} style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              <code>{r.table}</code> — {r.pending.toLocaleString('en-US')} rows past {r.days}d
            </div>
          ))}
          <button className="btn btn-muted" style={{ marginTop: 12, fontWeight: 600 }} disabled={busy || pendingSweep === 0}
            onClick={() => act(api.runStorageSweep,
              res => `Swept ${res.results.reduce((s, r) => s + (r.deleted || 0), 0).toLocaleString('en-US')} log rows`)}>
            {pendingSweep === 0 ? 'Nothing to sweep' : `Sweep ${pendingSweep.toLocaleString('en-US')} rows now`}
          </button>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700 }}>🗜 Compact database</div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 10px' }}>
            Rewrites the database with zero wasted pages and turns on automatic space
            reclamation{autoVacOn ? ' (already on)' : ' (currently OFF — this is the one-time fix)'}.
            Writes pause while it runs (typically under a minute). Do this during a quiet hour.
          </p>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Estimated reclaim: <strong>{fmtBytes(reclaimable)}</strong>
          </div>
          <button className="btn btn-gold" style={{ marginTop: 12, fontWeight: 700 }} disabled={busy}
            onClick={() => {
              if (!window.confirm('Compact the database now? Writes pause while it runs — use a quiet hour.')) return;
              act(api.runStorageCompact,
                res => `Compacted in ${res.seconds}s — reclaimed ${fmtBytes(res.reclaimed_bytes)}`);
            }}>
            {busy ? 'Working…' : 'Compact now'}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>Biggest tables (rows)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '4px 24px' }}>
          {report.top_tables.map(t => (
            <div key={t.table} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0', borderBottom: '1px solid var(--border-subtle, #edf2f7)' }}>
              <code style={{ color: 'var(--text-secondary)' }}>{t.table}</code>
              <span style={{ fontWeight: 700 }}>{t.rows.toLocaleString('en-US')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
